import { Link } from 'react-router-dom';
import { Cookie, ArrowLeft } from 'lucide-react';

export default function Cookies() {
  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <Cookie className="w-8 h-8 text-accent-blue" />
        <h1 className="text-3xl font-bold text-white">Cookie Policy</h1>
      </div>

      <div className="prose prose-invert max-w-none space-y-8">
        <p className="text-gray-400">
          <strong>Effective Date:</strong> January 1, 2025<br />
          <strong>Last Updated:</strong> January 26, 2025
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white">1. What Are Cookies?</h2>
          <p className="text-gray-300">
            Cookies are small text files stored on your device when you visit a website. They help websites
            remember your preferences and understand how you interact with content. Similar technologies include
            local storage and session storage.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">2. How We Use Cookies</h2>
          <p className="text-gray-300">
            Northcoast AI Academy uses cookies and similar technologies to:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Keep you logged in to your account</li>
            <li>Remember your preferences and settings</li>
            <li>Track your learning progress</li>
            <li>Understand how you use our platform</li>
            <li>Improve our services based on usage patterns</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">3. Types of Cookies We Use</h2>

          <div className="mt-4 space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white">Essential Cookies</h3>
              <p className="text-gray-300 text-sm mt-1">
                Required for the platform to function. Cannot be disabled.
              </p>
              <table className="w-full mt-2 text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="pb-2">Cookie</th>
                    <th className="pb-2">Purpose</th>
                    <th className="pb-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr>
                    <td className="py-1">auth_token</td>
                    <td>Authentication</td>
                    <td>7 days</td>
                  </tr>
                  <tr>
                    <td className="py-1">session_id</td>
                    <td>Session management</td>
                    <td>Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white">Functional Cookies</h3>
              <p className="text-gray-300 text-sm mt-1">
                Remember your preferences and personalization choices.
              </p>
              <table className="w-full mt-2 text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="pb-2">Cookie</th>
                    <th className="pb-2">Purpose</th>
                    <th className="pb-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr>
                    <td className="py-1">theme_pref</td>
                    <td>Display preferences</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td className="py-1">accessibility</td>
                    <td>Accessibility settings</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td className="py-1">font_size</td>
                    <td>Text size preference</td>
                    <td>1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white">Analytics Cookies</h3>
              <p className="text-gray-300 text-sm mt-1">
                Help us understand how visitors interact with our platform.
              </p>
              <table className="w-full mt-2 text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="pb-2">Cookie</th>
                    <th className="pb-2">Purpose</th>
                    <th className="pb-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr>
                    <td className="py-1">analytics_session</td>
                    <td>Session analytics</td>
                    <td>Session</td>
                  </tr>
                  <tr>
                    <td className="py-1">page_views</td>
                    <td>Usage tracking</td>
                    <td>30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">4. Local Storage</h2>
          <p className="text-gray-300">
            We also use browser local storage for:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li><strong>learning_progress:</strong> Tracks your position in lessons</li>
            <li><strong>draft_reflections:</strong> Saves unsubmitted work</li>
            <li><strong>ai_conversation:</strong> Stores recent AI chat history</li>
            <li><strong>quiz_answers:</strong> Saves quiz progress temporarily</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">5. Third-Party Cookies</h2>
          <p className="text-gray-300">
            Our platform may include cookies from:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li><strong>Stripe:</strong> Payment processing (fraud prevention)</li>
            <li><strong>Cloudflare:</strong> Security and performance</li>
          </ul>
          <p className="text-gray-300 mt-2">
            We do not use advertising cookies or tracking pixels from social media platforms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">6. Managing Cookies</h2>
          <p className="text-gray-300">
            You can manage cookies through your browser settings. Note that disabling essential cookies may
            affect platform functionality.
          </p>

          <div className="mt-4 space-y-2">
            <h3 className="text-lg font-medium text-white">Browser Settings</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>Chrome: Settings &gt; Privacy and Security &gt; Cookies</li>
              <li>Firefox: Settings &gt; Privacy & Security &gt; Cookies</li>
              <li>Safari: Preferences &gt; Privacy &gt; Cookies</li>
              <li>Edge: Settings &gt; Cookies and site permissions</li>
            </ul>
          </div>

          <div className="mt-4 space-y-2">
            <h3 className="text-lg font-medium text-white">Opt-Out Options</h3>
            <p className="text-gray-300">
              You can opt out of non-essential cookies in your account settings under
              "Privacy Preferences."
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">7. PWA and Offline Storage</h2>
          <p className="text-gray-300">
            If you install our Progressive Web App (PWA), we may store additional data locally for offline access:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Cached lesson content for offline reading</li>
            <li>Service worker data for app functionality</li>
            <li>Queued submissions for when connectivity returns</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">8. Updates to This Policy</h2>
          <p className="text-gray-300">
            We may update this Cookie Policy periodically to reflect changes in our practices or for legal compliance.
            Check this page for the latest information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">9. Contact Us</h2>
          <p className="text-gray-300">
            For questions about our cookie practices:
          </p>
          <p className="text-gray-300 mt-2">
            <strong>Email:</strong> privacy@northcoast.ai
          </p>
        </section>
      </div>
    </div>
  );
}
